import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Icon } from '@iconify/react';
import client from '../../api';

const PREVIEW_LIMIT = 5;
/** Set true hanya untuk simulasi UI lokal */
const SIMULATE_DUMMY = false;

const DUMMY_DO_COUNTS = [
  { do_number: 'DO-SIM-001', count: 5 },
  { do_number: 'DO-SIM-002', count: 4 },
  { do_number: 'DO-SIM-003', count: 7 }
];

const formatNumber = (value, fractionDigits) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '-';
  if (fractionDigits == null) {
    return num.toLocaleString('id-ID');
  }
  return num.toLocaleString('id-ID', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits
  });
};

const dimKey = item => {
  const l = Number(item?.length) || 0;
  const w = Number(item?.width) || 0;
  const h = Number(item?.height) || 0;
  const weight = Number(item?.weight) || 0;
  return `${l}x${w}x${h}|${weight}`;
};

const resolveUomLabel = item =>
  item?.uom?.name ||
  item?.uom?.remark ||
  item?.uom_remark ||
  item?.uom_name ||
  'Koli';

/**
 * Metrics per baris (setelah group dimensi + berat).
 * Dimensi mm — urutan & rumus selaras packing-list Sibp:
 * Weight → Total Weight → Weight Volume → Total Weight Volume
 * → Volume (m³) → Total Volume (m³) → Chargeable Weight
 */
const calcKubikasiMetrics = ({ length, width, height, weight, qty }) => {
  const p = Number(length) || 0;
  const l = Number(width) || 0;
  const t = Number(height) || 0;
  const w = Number(weight) || 0;
  const q = Number(qty) || 0;
  const volumeRaw = p * l * t;
  const totalWeight = q * w;
  const weightVolume = volumeRaw / 4000000;
  const totWeightVolume = weightVolume * q;
  const volumeM3 = volumeRaw / 1000000000;
  const totVolumeM3 = volumeM3 * q;
  const chargeableWeight = Math.max(totalWeight, totWeightVolume);

  return {
    totalWeight,
    weightVolume,
    totWeightVolume,
    volumeM3,
    totVolumeM3,
    chargeableWeight
  };
};

const buildDummyKubikasi = (doConfigs = DUMMY_DO_COUNTS) => {
  const sizes = [
    { length: 1200, width: 800, height: 1000, weight: 25 },
    { length: 1000, width: 800, height: 800, weight: 18 },
    { length: 1500, width: 900, height: 1100, weight: 32 },
    { length: 800, width: 600, height: 700, weight: 12 },
    { length: 1100, width: 700, height: 900, weight: 20 },
    { length: 1300, width: 850, height: 950, weight: 28 },
    { length: 900, width: 900, height: 900, weight: 15 },
    { length: 1600, width: 1000, height: 1200, weight: 40 },
    { length: 700, width: 500, height: 600, weight: 8 },
    { length: 1400, width: 800, height: 1000, weight: 30 },
    { length: 1050, width: 750, height: 850, weight: 22 },
    { length: 1250, width: 820, height: 980, weight: 26 }
  ];

  return doConfigs.flatMap((config, doIndex) =>
    Array.from({ length: config.count }, (_, index) => {
      const size = sizes[(index + doIndex * 3) % sizes.length];
      return {
        id: `dummy-${config.do_number}-${index + 1}`,
        do_number: config.do_number,
        length: size.length,
        width: size.width,
        height: size.height,
        weight: size.weight + doIndex * 2,
        uom: { name: 'Koli', remark: 'Koli' }
      };
    })
  );
};

const ListKubikasi = ({ jobcostingId, sdlLabel, loadsLabel }) => {
  const { user } = useSelector(state => state.auth);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  /** DO mana yang body/tabelnya terbuka */
  const [openDos, setOpenDos] = useState({});
  /** Dalam DO: tampilkan semua baris ukuran (bukan preview) */
  const [expandedRows, setExpandedRows] = useState({});

  useEffect(() => {
    let active = true;

    const fetchKubikasi = async () => {
      if (SIMULATE_DUMMY) {
        setList(buildDummyKubikasi());
        setLoading(false);
        return;
      }

      if (!jobcostingId) {
        setList([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await client.get(
          `/api/customers/order/kubikasi?jobcosting_id=${jobcostingId}`,
          {
            headers: {
              'Content-type': 'application/json',
              Authorization: `Bearer ${user.data.token}`
            }
          }
        );
        if (!active) return;
        const data = response?.data;
        setList(
          Array.isArray(data?.data)
            ? data.data
            : Array.isArray(data)
            ? data
            : []
        );
      } catch {
        if (active) setList([]);
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchKubikasi();
    return () => {
      active = false;
    };
  }, [jobcostingId, user?.data?.token]);

  const groupedList = useMemo(() => {
    const map = new Map();

    list.forEach(item => {
      const key = String(item?.do_number || '').trim() || '-';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(item);
    });

    return Array.from(map.entries()).map(([do_number, items]) => {
      const dimMap = new Map();

      items.forEach(item => {
        const key = dimKey(item);
        if (!dimMap.has(key)) {
          dimMap.set(key, {
            key,
            length: item?.length,
            width: item?.width,
            height: item?.height,
            weight: Number(item?.weight) || 0,
            qty: 0
          });
        }
        dimMap.get(key).qty += 1;
      });

      const dimensionGroups = Array.from(dimMap.values())
        .map(row => ({
          ...row,
          ...calcKubikasiMetrics(row)
        }))
        .sort((a, b) => b.qty - a.qty);

      const totals = dimensionGroups.reduce(
        (acc, row) => {
          acc.totalWeight += row.totalWeight;
          acc.totWeightVolume += row.totWeightVolume;
          acc.totVolumeM3 += row.totVolumeM3;
          acc.totalChargeable += row.chargeableWeight;
          return acc;
        },
        {
          totalWeight: 0,
          totWeightVolume: 0,
          totVolumeM3: 0,
          totalChargeable: 0
        }
      );

      return {
        do_number,
        items,
        dimensionGroups,
        ...totals,
        uomLabel: resolveUomLabel(items[0])
      };
    });
  }, [list]);

  const grandSummary = useMemo(() => {
    const uomCounts = new Map();

    const summary = groupedList.reduce(
      (acc, group) => {
        acc.doCount += 1;
        acc.itemCount += group.items.length;
        acc.totalWeight += group.totalWeight;
        acc.totWeightVolume += group.totWeightVolume;
        acc.totVolumeM3 += group.totVolumeM3;
        acc.totalChargeable += group.totalChargeable;

        group.items.forEach(item => {
          const label = resolveUomLabel(item);
          uomCounts.set(label, (uomCounts.get(label) || 0) + 1);
        });

        return acc;
      },
      {
        doCount: 0,
        itemCount: 0,
        totalWeight: 0,
        totWeightVolume: 0,
        totVolumeM3: 0,
        totalChargeable: 0
      }
    );

    // Contoh: "16 Koli" atau "10 Koli · 6 Box" sesuai UOM di list
    summary.qtyLabel = Array.from(uomCounts.entries())
      .map(([label, count]) => `${formatNumber(count)} ${label}`)
      .join(' · ');

    return summary;
  }, [groupedList]);

  const firstDoNumber = groupedList[0]?.do_number;

  const headerTitle = [sdlLabel, loadsLabel].filter(Boolean).join(' - ');

  if (loading) {
    return <div className='checkpoint-empty'>Memuat kubikasi...</div>;
  }

  if (!list.length) {
    return (
      <div className='kubikasi-panel'>
        {headerTitle ? (
          <p className='kubikasi-panel__subtitle'>{headerTitle}</p>
        ) : null}
        <div className='checkpoint-empty'>Belum ada data kubikasi</div>
      </div>
    );
  }

  return (
    <div className='kubikasi-panel'>
      {headerTitle ? (
        <p className='kubikasi-panel__subtitle'>{headerTitle}</p>
      ) : null}

      {SIMULATE_DUMMY ? (
        <p className='kubikasi-panel__hint'>
          Mode simulasi: {formatNumber(grandSummary.doCount)} DO ·{' '}
          {grandSummary.qtyLabel} (5 + 4 + 7)
        </p>
      ) : null}

      <div
        className={`kubikasi-stack${
          grandSummary.doCount > 1 ? ' has-summary' : ''
        }`}>
        {grandSummary.doCount > 1 ? (
          <div className='kubikasi-grand'>
            <div className='kubikasi-grand__lead'>
              <p className='kubikasi-grand__eyebrow'>Ringkasan</p>
              <p className='kubikasi-grand__title'>
                {formatNumber(grandSummary.doCount)} DO ·{' '}
                {grandSummary.qtyLabel}
              </p>
            </div>
            <dl className='kubikasi-grand__metrics'>
              <div className='kubikasi-grand__metric'>
                <dt>Total Weight</dt>
                <dd>{formatNumber(grandSummary.totalWeight, 2)}</dd>
              </div>
              <div className='kubikasi-grand__metric'>
                <dt>Total Weight Volume</dt>
                <dd>{formatNumber(grandSummary.totWeightVolume, 2)}</dd>
              </div>
              <div className='kubikasi-grand__metric'>
                <dt>Total Volume (m³)</dt>
                <dd>{formatNumber(grandSummary.totVolumeM3, 2)}</dd>
              </div>
              <div className='kubikasi-grand__metric is-chargeable'>
                <dt>Total Chargeable</dt>
                <dd>{formatNumber(grandSummary.totalChargeable, 2)}</dd>
              </div>
            </dl>
          </div>
        ) : null}

        <div className='kubikasi-stack__body'>
          {grandSummary.doCount > 1 ? (
            <p className='kubikasi-stack__section'>Rincian per DO</p>
          ) : null}

          <div className='kubikasi-group-list'>
            {groupedList.map(group => {
              const canCollapse = groupedList.length > 1;
              // 1 DO: selalu terbuka + tanpa tombol expand
              // >1 DO: default DO pertama terbuka
              const isOpen = canCollapse
                ? Object.prototype.hasOwnProperty.call(
                    openDos,
                    group.do_number
                  )
                  ? !!openDos[group.do_number]
                  : group.do_number === firstDoNumber
                : true;
              const showAllRows = !!expandedRows[group.do_number];
              const hasMore = group.dimensionGroups.length > PREVIEW_LIMIT;
              const hiddenCount =
                group.dimensionGroups.length - PREVIEW_LIMIT;
              const visibleGroups =
                hasMore && !showAllRows
                  ? group.dimensionGroups.slice(0, PREVIEW_LIMIT)
                  : group.dimensionGroups;

              const toggleOpen = () => {
                if (!canCollapse) return;
                setOpenDos(prev => ({
                  ...prev,
                  [group.do_number]: !isOpen
                }));
              };

              const HeaderTag = canCollapse ? 'button' : 'div';
              const headerProps = canCollapse
                ? {
                    type: 'button',
                    'aria-expanded': isOpen,
                    onClick: toggleOpen
                  }
                : {};

              return (
                <div
                  key={group.do_number}
                  className={`kubikasi-group${isOpen ? ' is-open' : ''}${
                    canCollapse ? '' : ' is-static'
                  }`}>
                  <HeaderTag
                    className='kubikasi-group__header'
                    {...headerProps}>
                    <div className='kubikasi-group__header-left'>
                      <span className='kubikasi-group__do-chip'>
                        DO {group.do_number}
                      </span>
                      <p className='kubikasi-group__title'>
                        {formatNumber(group.items.length)} {group.uomLabel}
                      </p>
                    </div>
                    <div className='kubikasi-group__header-right'>
                      <div className='kubikasi-group__header-stats'>
                        <div className='kubikasi-group__stat'>
                          <span className='kubikasi-group__stat-label'>
                            Total Weight
                          </span>
                          <span className='kubikasi-group__stat-value'>
                            {formatNumber(group.totalWeight, 2)}
                          </span>
                        </div>
                        <div className='kubikasi-group__stat'>
                          <span className='kubikasi-group__stat-label'>
                            Total Weight Volume
                          </span>
                          <span className='kubikasi-group__stat-value'>
                            {formatNumber(group.totWeightVolume, 2)}
                          </span>
                        </div>
                        <div className='kubikasi-group__stat'>
                          <span className='kubikasi-group__stat-label'>
                            Total Volume (m³)
                          </span>
                          <span className='kubikasi-group__stat-value'>
                            {formatNumber(group.totVolumeM3, 2)}
                          </span>
                        </div>
                        <div className='kubikasi-group__stat is-chargeable'>
                          <span className='kubikasi-group__stat-label'>
                            Total Chargeable
                          </span>
                          <span className='kubikasi-group__stat-value'>
                            {formatNumber(group.totalChargeable, 2)}
                          </span>
                        </div>
                      </div>
                      {canCollapse ? (
                        <span className='kubikasi-group__expand' aria-hidden>
                          <Icon
                            icon={
                              isOpen
                                ? 'solar:alt-arrow-up-bold'
                                : 'solar:alt-arrow-down-bold'
                            }
                            width={18}
                          />
                        </span>
                      ) : null}
                    </div>
                  </HeaderTag>

                  {isOpen ? (
                    <div className='kubikasi-group__body'>
                      <div className='kubikasi-table-wrap'>
                        <table className='kubikasi-table kubikasi-table--dense'>
                          <thead>
                            <tr>
                              <th className='is-no'>No</th>
                              <th>Dimensi (P × L × T)</th>
                              <th className='is-right'>Qty</th>
                              <th
                                className='is-right'
                                title='Weight per koli'>
                                Weight
                              </th>
                              <th
                                className='is-right'
                                title='Total Weight = qty × weight'>
                                Total Weight
                              </th>
                              <th
                                className='is-right'
                                title='Weight Volume = (P × L × T mm) / 4.000.000'>
                                Weight Volume
                              </th>
                              <th
                                className='is-right'
                                title='Total Weight Volume = Weight Volume × qty'>
                                Total Weight Volume
                              </th>
                              <th
                                className='is-right'
                                title='Volume (m³) = (P × L × T mm) / 1.000.000.000'>
                                Volume (m³)
                              </th>
                              <th
                                className='is-right'
                                title='Total Volume (m³) = Volume × qty'>
                                Total Volume (m³)
                              </th>
                              <th
                                className='is-right'
                                title='Chargeable Weight = MAX(Total Weight, Total Weight Volume)'>
                                Chargeable Weight
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {visibleGroups.map((row, idx) => (
                              <tr key={row.key}>
                                <td className='is-no'>{idx + 1}.</td>
                                <td>
                                  {formatNumber(row.length)} ×{' '}
                                  {formatNumber(row.width)} ×{' '}
                                  {formatNumber(row.height)} mm
                                </td>
                                <td className='is-right'>
                                  {formatNumber(row.qty)}
                                </td>
                                <td className='is-right'>
                                  {formatNumber(row.weight, 2)}
                                </td>
                                <td className='is-right'>
                                  {formatNumber(row.totalWeight, 2)}
                                </td>
                                <td className='is-right'>
                                  {formatNumber(row.weightVolume, 2)}
                                </td>
                                <td className='is-right'>
                                  {formatNumber(row.totWeightVolume, 2)}
                                </td>
                                <td className='is-right'>
                                  {formatNumber(row.volumeM3, 2)}
                                </td>
                                <td className='is-right'>
                                  {formatNumber(row.totVolumeM3, 2)}
                                </td>
                                <td className='is-right is-strong'>
                                  {formatNumber(row.chargeableWeight, 2)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {hasMore ? (
                        <div className='kubikasi-detail-block'>
                          <button
                            type='button'
                            className={`kubikasi-group__toggle${
                              showAllRows ? ' is-open' : ''
                            }`}
                            aria-expanded={showAllRows}
                            onClick={() =>
                              setExpandedRows(prev => ({
                                ...prev,
                                [group.do_number]: !showAllRows
                              }))
                            }>
                            <span className='kubikasi-group__toggle-left'>
                              <Icon
                                icon={
                                  showAllRows
                                    ? 'solar:eye-closed-bold'
                                    : 'solar:eye-bold'
                                }
                                width={16}
                              />
                              <span className='kubikasi-group__toggle-copy'>
                                <span className='kubikasi-group__toggle-title'>
                                  {showAllRows
                                    ? 'Sembunyikan'
                                    : `Lihat semua (${formatNumber(
                                        hiddenCount
                                      )} lagi)`}
                                </span>
                                <span className='kubikasi-group__toggle-meta'>
                                  Menampilkan{' '}
                                  {formatNumber(visibleGroups.length)} dari{' '}
                                  {formatNumber(
                                    group.dimensionGroups.length
                                  )}{' '}
                                  ukuran
                                </span>
                              </span>
                            </span>
                            <Icon
                              icon={
                                showAllRows
                                  ? 'solar:alt-arrow-up-bold'
                                  : 'solar:alt-arrow-down-bold'
                              }
                              width={16}
                            />
                          </button>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListKubikasi;
